export interface TermsSection {
  id: string;
  title: string;
  content: string[];
  subsections?: {
    title: string;
    content: string[];
  }[];
}

export interface TermsMetadata {
  effectiveDate: string;
  lastUpdated: string;
  version: string;
  jurisdiction: string;
}

export const termsMetadata: TermsMetadata = {
  effectiveDate: "January 1, 2025",
  lastUpdated: "January 15, 2025",
  version: "3.2",
  jurisdiction: "State of Delaware, United States",
};

export const termsTableOfContents = [
  { id: "acceptance", title: "Acceptance of Terms" },
  { id: "eligibility", title: "Eligibility Requirements" },
  { id: "account-registration", title: "Account Registration & Verification" },
  { id: "identity-verification", title: "Identity Verification Process" },
  { id: "personal-documents", title: "Personal Documents & Data Collection" },
  { id: "privacy-data", title: "Privacy & Data Protection" },
  { id: "user-obligations", title: "User Obligations & Conduct" },
  { id: "intellectual-property", title: "Intellectual Property Rights" },
  { id: "payment-terms", title: "Payment Terms & Compensation" },
  { id: "confidentiality", title: "Confidentiality & Non-Disclosure" },
  { id: "termination", title: "Termination & Suspension" },
  { id: "disclaimers", title: "Disclaimers & Limitations" },
  { id: "indemnification", title: "Indemnification" },
  { id: "dispute-resolution", title: "Dispute Resolution & Arbitration" },
  { id: "modifications", title: "Modifications to Terms" },
  { id: "contact", title: "Contact Information" },
];

export const termsSections: TermsSection[] = [
  {
    id: "acceptance",
    title: "1. Acceptance of Terms",
    content: [
      'Welcome to Unimerg ("Company," "we," "us," or "our"). These Terms of Service ("Terms") constitute a legally binding agreement between you ("User," "Contributor," "you," or "your") and Unimerg Inc., governing your access to and use of our platform, website, applications, and services (collectively, the "Services").',
      "By creating an account, accessing, or using our Services, you acknowledge that you have read, understood, and agree to be bound by these Terms, our Privacy Policy, our Acceptable Use Policy, and all other policies and guidelines incorporated herein by reference. If you do not agree to these Terms, you must not access or use our Services.",
      "These Terms apply to all visitors, users, contributors, and others who access or use the Services. By using the Services, you represent and warrant that you have the legal capacity to enter into a binding agreement and that you are not prohibited from receiving services under the laws of any applicable jurisdiction.",
    ],
    subsections: [
      {
        title: "1.1 Electronic Agreement",
        content: [
          'You agree that by clicking "I Accept," "Sign Up," "Submit," or similar buttons, or by accessing or using our Services, you are entering into a legally binding contract. This electronic acceptance has the same legal effect as a physical signature.',
          "You consent to receive all communications, agreements, documents, receipts, notices, and disclosures electronically, including via email or through our platform.",
        ],
      },
      {
        title: "1.2 Additional Terms",
        content: [
          "Certain features or services may be subject to additional terms, guidelines, or rules, which will be posted on or within the Services. All such additional terms are incorporated by reference into these Terms.",
          "In the event of a conflict between these Terms and any additional terms, the additional terms shall control for that specific feature or service.",
        ],
      },
    ],
  },
  {
    id: "eligibility",
    title: "2. Eligibility Requirements",
    content: [
      "To use our Services, you must meet the following eligibility requirements. Failure to meet these requirements may result in denial of access or termination of your account.",
    ],
    subsections: [
      {
        title: "2.1 Age Requirements",
        content: [
          "You must be at least eighteen (18) years of age to create an account and use our Services. In jurisdictions where the age of majority is higher than eighteen, you must meet the age of majority in your jurisdiction.",
          "By using the Services, you represent and warrant that you meet the applicable age requirements. We reserve the right to request proof of age at any time and to terminate accounts of users who misrepresent their age.",
        ],
      },
      {
        title: "2.2 Legal Capacity",
        content: [
          "You must have the legal capacity to enter into binding contracts in your jurisdiction. You represent that you are not legally prohibited from using the Services under any applicable law.",
          "If you are using the Services on behalf of an organization, you represent and warrant that you have the authority to bind that organization to these Terms.",
        ],
      },
      {
        title: "2.3 Geographic Restrictions",
        content: [
          "Our Services are available in over 150 countries. However, due to regulatory requirements, sanctions, or other legal restrictions, we cannot provide Services to users in certain jurisdictions. A current list of restricted regions is available on our website.",
          "You represent that you are not located in, under the control of, or a national or resident of any restricted country or territory, and that you are not on any government list of prohibited or restricted parties.",
        ],
      },
      {
        title: "2.4 Technical Requirements",
        content: [
          "You must have access to a compatible device, reliable internet connection, and meet the minimum technical requirements as specified on our platform. You are responsible for obtaining and maintaining all equipment and services needed to access the Services.",
        ],
      },
    ],
  },
  {
    id: "account-registration",
    title: "3. Account Registration & Verification",
    content: [
      "To access most features of our Services, you must create an account. The registration process requires accurate information and may involve multiple verification steps to ensure platform security and regulatory compliance.",
    ],
    subsections: [
      {
        title: "3.1 Registration Requirements",
        content: [
          "When creating an account, you must provide accurate, current, and complete information, including but not limited to: your full legal name as it appears on official government-issued identification, a valid email address, your current residential address, date of birth, phone number, and any other information requested during registration.",
          "You agree to promptly update your account information to keep it accurate, current, and complete. Failure to do so may result in suspension or termination of your account.",
        ],
      },
      {
        title: "3.2 Gate Code System",
        content: [
          "Access to our platform may require a valid gate code, which is provided to approved applicants or through authorized referrals. Gate codes are single-use, non-transferable, and must be used within the specified validity period.",
          "Sharing, selling, or misusing gate codes is strictly prohibited and may result in permanent ban from the platform. We reserve the right to revoke gate codes at any time without notice.",
        ],
      },
      {
        title: "3.3 Account Security",
        content: [
          "You are responsible for maintaining the confidentiality of your account credentials, including your password and any authentication methods. You must immediately notify us of any unauthorized use of your account or any other security breach.",
          "You agree not to share your account credentials with any third party. You are solely responsible for all activities that occur under your account, whether or not authorized by you.",
          "We strongly recommend enabling two-factor authentication (2FA) to enhance your account security. We are not liable for any loss or damage arising from your failure to protect your account credentials.",
        ],
      },
      {
        title: "3.4 Single Account Policy",
        content: [
          "Each individual may only maintain one active account on our platform. Creating multiple accounts, using another person's account, or allowing others to use your account is strictly prohibited.",
          "We employ various technical measures to detect multiple accounts. Violations of this policy will result in permanent termination of all associated accounts and forfeiture of any pending earnings.",
        ],
      },
    ],
  },
  {
    id: "identity-verification",
    title: "4. Identity Verification Process",
    content: [
      "To maintain platform integrity, prevent fraud, comply with legal requirements, and ensure the quality of our contributor network, we require comprehensive identity verification. This process is mandatory and must be completed before you can access certain features or receive payments.",
    ],
    subsections: [
      {
        title: "4.1 Verification Levels",
        content: [
          "Our platform employs a tiered verification system. Level 1 (Basic) requires email verification and phone number confirmation. Level 2 (Standard) requires government-issued photo identification and facial recognition verification. Level 3 (Enhanced) requires additional documentation such as proof of address, professional credentials, and may include video verification.",
          "Certain projects, payment thresholds, or features may require higher verification levels. You will be notified of verification requirements before accessing specific features.",
        ],
      },
      {
        title: "4.2 Government-Issued Identification",
        content: [
          "You must provide a valid, unexpired government-issued photo identification document. Acceptable documents include: national ID card, passport, driver's license, or other government-issued identification as specified during the verification process.",
          "The identification document must clearly show: your full legal name, date of birth, photograph, document number, issue date, and expiration date. Documents that are expired, damaged, illegible, or appear altered will be rejected.",
          "We may use automated systems and third-party verification services to validate the authenticity of submitted documents. Submitting fraudulent, forged, or altered documents is a criminal offense and will be reported to appropriate authorities.",
        ],
      },
      {
        title: "4.3 Facial Recognition & Biometric Verification",
        content: [
          "As part of our verification process, you may be required to submit photographs or video of yourself for facial recognition matching against your submitted identification documents. This process helps prevent identity fraud and unauthorized account access.",
          "By proceeding with verification, you consent to the collection, processing, and storage of your biometric data for identity verification purposes. Biometric data is processed in accordance with our Privacy Policy and applicable laws.",
          "You must submit clear, unobstructed images or video as instructed. Attempts to circumvent biometric verification through photographs, masks, or other methods will result in immediate account termination.",
        ],
      },
      {
        title: "4.4 Proof of Address",
        content: [
          "For certain verification levels or payment methods, you must provide proof of your current residential address. Acceptable documents include: utility bills (electricity, gas, water, internet), bank or financial statements, government correspondence, or tax documents.",
          "Documents must be dated within the last three (3) months, clearly show your full name and address, and match the information provided in your account. P.O. Box addresses may not be accepted for all purposes.",
        ],
      },
      {
        title: "4.5 Professional Credentials",
        content: [
          "Certain specialized projects may require verification of professional credentials, educational qualifications, or specialized certifications. You may be asked to provide: diplomas, certificates, professional licenses, or other documentation proving your qualifications.",
          "We may verify credentials directly with issuing institutions or through third-party verification services. Misrepresenting your qualifications is grounds for immediate termination and may have legal consequences.",
        ],
      },
      {
        title: "4.6 Video Verification",
        content: [
          "In some cases, you may be required to complete a live or recorded video verification session. During this process, you may be asked to show your identification document, answer security questions, perform specific actions, or repeat randomly generated phrases.",
          "Video verification sessions may be recorded and stored for security and compliance purposes. Failure to complete video verification when requested may result in account restrictions.",
        ],
      },
      {
        title: "4.7 Ongoing Verification",
        content: [
          "Identity verification is not a one-time process. We may request re-verification at any time, including when: your documents expire, you request changes to account information, unusual activity is detected, regulatory requirements change, or you reach certain payment thresholds.",
          "You agree to cooperate with ongoing verification requests promptly. Failure to complete re-verification within the specified timeframe may result in account suspension.",
        ],
      },
    ],
  },
  {
    id: "personal-documents",
    title: "5. Personal Documents & Data Collection",
    content: [
      "Our Services require the collection, processing, and storage of various personal documents and information. This section details what information we collect, why we collect it, and how it is used.",
    ],
    subsections: [
      {
        title: "5.1 Types of Documents Collected",
        content: [
          "During your use of our Services, you may be required to submit the following types of documents and information:",
          "Identity Documents: Government-issued photo identification (passport, national ID, driver's license), secondary identification documents, and photographs or video for facial verification.",
          "Address Verification Documents: Utility bills, bank statements, government correspondence, lease agreements, or other documents proving residential address.",
          "Financial Documents: Tax identification numbers (SSN, TIN, or equivalent), tax forms (W-9, W-8BEN, or equivalent), bank account information, payment processor account details.",
          "Professional Documents: Educational certificates, professional licenses, employment verification, certifications, and credentials relevant to specialized projects.",
          "Additional Documentation: Any other documents reasonably required for verification, compliance, or service provision.",
        ],
      },
      {
        title: "5.2 Purpose of Collection",
        content: [
          "Personal documents are collected for the following purposes: identity verification and fraud prevention, regulatory compliance (including KYC/AML requirements), tax reporting and compliance, payment processing, quality assurance and platform integrity, dispute resolution, and legal compliance.",
          "We only collect documents that are necessary for the stated purposes. You will be informed of the specific purpose when documents are requested.",
        ],
      },
      {
        title: "5.3 Document Submission Requirements",
        content: [
          "All submitted documents must be: authentic and unaltered, legible and of sufficient quality, in an accepted file format (PDF, JPEG, PNG as specified), within size limits specified during upload, and in the original language with certified translation if required.",
          "You certify that all documents submitted are genuine, accurate, and belong to you or are authorized for your use. Submitting false, fraudulent, or unauthorized documents is strictly prohibited.",
        ],
      },
      {
        title: "5.4 Document Retention",
        content: [
          "We retain personal documents for the period necessary to fulfill the purposes for which they were collected, comply with legal obligations, resolve disputes, and enforce our agreements. Retention periods vary based on document type and applicable legal requirements.",
          "Identity verification documents may be retained for a minimum of five (5) years after account closure. Tax-related documents are retained as required by applicable tax authorities (typically seven years or more).",
          "You may request deletion of your documents subject to our legal retention requirements. We will inform you of any documents that must be retained and the applicable retention period.",
        ],
      },
      {
        title: "5.5 Document Security",
        content: [
          "All personal documents are protected using industry-standard security measures including: encryption in transit (TLS 1.3 or higher) and at rest (AES-256), access controls and authentication requirements, secure data centers with physical security measures, regular security audits and penetration testing, and employee training and access restrictions.",
          "Despite our security measures, no system is completely secure. You acknowledge and accept the inherent risks of transmitting documents electronically.",
        ],
      },
      {
        title: "5.6 Third-Party Verification Services",
        content: [
          "We may use third-party services to assist with identity verification, document authentication, and fraud detection. These services may have access to your submitted documents and personal information solely for verification purposes.",
          "Third-party service providers are bound by confidentiality obligations and data protection agreements. A list of our verification partners is available upon request.",
        ],
      },
    ],
  },
  {
    id: "privacy-data",
    title: "6. Privacy & Data Protection",
    content: [
      "Your privacy is important to us. This section summarizes key privacy practices. For complete details, please refer to our comprehensive Privacy Policy.",
    ],
    subsections: [
      {
        title: "6.1 Data Collection",
        content: [
          "We collect personal information that you provide directly, information collected automatically through your use of the Services, and information from third-party sources as described in our Privacy Policy.",
          "Categories of personal information collected include: identifiers, contact information, financial information, professional information, biometric data, device and usage information, and geolocation data.",
        ],
      },
      {
        title: "6.2 Data Use",
        content: [
          "We use your personal information to: provide and improve our Services, verify your identity, process payments, communicate with you, comply with legal obligations, prevent fraud and abuse, and for other purposes disclosed at the time of collection or with your consent.",
        ],
      },
      {
        title: "6.3 Data Sharing",
        content: [
          "We may share your personal information with: service providers who assist in our operations, business partners and clients (for project-related purposes), legal and regulatory authorities when required, affiliated companies within our corporate group, and other parties with your consent or as otherwise permitted by law.",
          "We do not sell your personal information to third parties for their direct marketing purposes.",
        ],
      },
      {
        title: "6.4 Your Rights",
        content: [
          "Depending on your jurisdiction, you may have certain rights regarding your personal information, including: the right to access, correct, or delete your data, the right to data portability, the right to opt out of certain processing, and the right to withdraw consent.",
          "To exercise your rights, please contact us using the information provided at the end of these Terms or through your account settings.",
        ],
      },
      {
        title: "6.5 International Data Transfers",
        content: [
          "Your personal information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws. We implement appropriate safeguards for international transfers as required by applicable law.",
        ],
      },
      {
        title: "6.6 Data Breach Notification",
        content: [
          "In the event of a data breach affecting your personal information, we will notify you and relevant authorities as required by applicable law. Notification will include the nature of the breach, types of information affected, and steps you can take to protect yourself.",
        ],
      },
    ],
  },
  {
    id: "user-obligations",
    title: "7. User Obligations & Conduct",
    content: [
      "As a user of our Services, you agree to abide by certain obligations and standards of conduct. Violation of these obligations may result in account suspension, termination, and legal action.",
    ],
    subsections: [
      {
        title: "7.1 General Conduct",
        content: [
          "You agree to: use the Services only for lawful purposes, comply with all applicable laws and regulations, provide accurate and truthful information, treat other users and our staff with respect, follow project guidelines and instructions, and maintain the quality standards expected on the platform.",
        ],
      },
      {
        title: "7.2 Prohibited Activities",
        content: [
          "You shall not: submit false, misleading, or fraudulent information, use the Services for any illegal or unauthorized purpose, attempt to circumvent security measures or verification processes, access accounts or information belonging to others, use automated tools or bots without authorization, engage in activities that disrupt or burden the Services, harass, threaten, or harm other users, violate intellectual property rights, share confidential project information, create multiple accounts, or engage in any form of fraud.",
        ],
      },
      {
        title: "7.3 Quality Standards",
        content: [
          "You agree to maintain high quality standards in all work submitted through the platform. This includes: following project instructions carefully, providing accurate and thoughtful responses, meeting deadlines and time requirements, maintaining consistency in your work, and reporting any issues or concerns promptly.",
          "Consistent failure to meet quality standards may result in reduced access to projects, lower compensation rates, or account termination.",
        ],
      },
      {
        title: "7.4 Compliance with Project Requirements",
        content: [
          "Individual projects may have specific requirements, guidelines, and restrictions. You agree to read, understand, and comply with all project-specific requirements before accepting tasks.",
          "If you are unable to comply with project requirements, you should not accept the task. Accepting tasks with no intention or ability to comply is a violation of these Terms.",
        ],
      },
    ],
  },
  {
    id: "intellectual-property",
    title: "8. Intellectual Property Rights",
    content: [
      "This section addresses intellectual property rights related to the Services and work created on the platform.",
    ],
    subsections: [
      {
        title: "8.1 Unimerg Intellectual Property",
        content: [
          "The Services, including all content, features, and functionality, are owned by Unimerg and protected by copyright, trademark, patent, and other intellectual property laws. Our name, logo, and all related marks are trademarks of Unimerg Inc.",
          "You may not copy, modify, distribute, sell, or lease any part of our Services or included software without our express written permission.",
        ],
      },
      {
        title: "8.2 Work Product Ownership",
        content: [
          'All work product created through your use of the Services, including but not limited to annotations, evaluations, training data, responses, and any other contributions ("Work Product"), shall be owned exclusively by Unimerg or our clients as specified in the applicable project terms.',
          "You hereby assign to Unimerg all right, title, and interest in and to any Work Product you create, including all intellectual property rights therein. This assignment is irrevocable and applies worldwide.",
          "You waive any moral rights in the Work Product to the fullest extent permitted by law. You agree not to assert any claims against Unimerg or our clients regarding the Work Product.",
        ],
      },
      {
        title: "8.3 License to User Content",
        content: [
          "For any content you submit that is not Work Product (such as profile information, feedback, or forum posts), you grant Unimerg a non-exclusive, worldwide, royalty-free, transferable license to use, reproduce, modify, and display such content in connection with the Services.",
        ],
      },
      {
        title: "8.4 Third-Party Content",
        content: [
          "Projects may involve content owned by third parties. You agree to respect third-party intellectual property rights and to use such content only as authorized within the scope of the project.",
        ],
      },
    ],
  },
  {
    id: "payment-terms",
    title: "9. Payment Terms & Compensation",
    content: [
      "This section governs compensation for work completed on the platform.",
    ],
    subsections: [
      {
        title: "9.1 Compensation Structure",
        content: [
          "Compensation rates are determined by project type, task complexity, your qualifications, and performance history. Rates are displayed before you accept tasks and are subject to change.",
          "Unimerg reserves the right to modify compensation rates at any time. Rate changes will not affect tasks already in progress at the time of the change.",
        ],
      },
      {
        title: "9.2 Payment Processing",
        content: [
          "Payments are processed weekly for work completed and approved through the previous Sunday. You must have a valid payment method on file and meet the minimum payout threshold of $25 to receive payment.",
          "Available payment methods include PayPal, direct bank transfer (where available), Wise, and Payoneer. Available methods vary by country. You are responsible for any fees charged by your payment provider.",
        ],
      },
      {
        title: "9.3 Payment Timing",
        content: [
          "Payments are typically processed within 3-5 business days after the weekly processing date. Delays may occur due to: bank processing times, payment provider issues, verification requirements, holidays, or account issues requiring resolution.",
          "We are not responsible for delays caused by third-party payment providers or your financial institution.",
        ],
      },
      {
        title: "9.4 Tax Obligations",
        content: [
          "You are solely responsible for all taxes associated with your earnings, including income tax, self-employment tax, VAT, GST, or other applicable taxes. Unimerg does not withhold taxes except where required by law.",
          "US-based contributors will receive appropriate tax forms (1099) if earnings exceed IRS thresholds. You are responsible for providing accurate tax information (W-9, W-8BEN, or equivalent).",
          "You agree to indemnify Unimerg for any taxes, penalties, or interest resulting from your failure to properly report income or comply with tax obligations.",
        ],
      },
      {
        title: "9.5 Disputed Payments",
        content: [
          "If you believe your compensation is incorrect, you must notify us within thirty (30) days of the payment date. Disputes submitted after this period may not be honored.",
          "We will investigate payment disputes and respond within fourteen (14) business days. Our determination of disputed amounts is final unless manifest error is demonstrated.",
        ],
      },
      {
        title: "9.6 Forfeiture of Earnings",
        content: [
          "Earnings may be forfeited if: your account is terminated for violation of these Terms, work is determined to be fraudulent or of unacceptable quality, payments were made in error, or you fail to claim earnings within twelve (12) months.",
          "You acknowledge that forfeited earnings cannot be recovered, and you release any claims to such earnings.",
        ],
      },
    ],
  },
  {
    id: "confidentiality",
    title: "10. Confidentiality & Non-Disclosure",
    content: [
      "Access to our platform may expose you to confidential information. You agree to maintain strict confidentiality as set forth in this section.",
    ],
    subsections: [
      {
        title: "10.1 Confidential Information",
        content: [
          '"Confidential Information" includes but is not limited to: project content and materials, client identities and information, proprietary methodologies and processes, business strategies and plans, pricing and financial information, user and contributor data, and any information marked or reasonably understood to be confidential.',
        ],
      },
      {
        title: "10.2 Confidentiality Obligations",
        content: [
          "You agree to: keep all Confidential Information strictly confidential, not disclose Confidential Information to any third party, not use Confidential Information except as necessary to perform tasks, protect Confidential Information using reasonable security measures, and promptly notify us of any unauthorized disclosure.",
          "These confidentiality obligations survive termination of your account and continue indefinitely, or until the information becomes publicly available through no fault of yours.",
        ],
      },
      {
        title: "10.3 Exceptions",
        content: [
          "Confidentiality obligations do not apply to information that: was already known to you before disclosure, becomes publicly available through no fault of yours, is independently developed by you without use of Confidential Information, or is required to be disclosed by law (provided you give us reasonable notice).",
        ],
      },
      {
        title: "10.4 Return of Materials",
        content: [
          "Upon request or termination of your account, you must promptly return or destroy all Confidential Information in your possession, including any copies, notes, or derivatives. You must certify destruction in writing if requested.",
        ],
      },
    ],
  },
  {
    id: "termination",
    title: "11. Termination & Suspension",
    content: [
      "This section describes the circumstances under which your account may be terminated or suspended.",
    ],
    subsections: [
      {
        title: "11.1 Termination by You",
        content: [
          "You may terminate your account at any time by contacting support or using the account deletion feature in your settings. Upon termination: access to the Services will cease, pending earnings will be paid according to normal payment schedules (minus any disputed amounts), and your data will be handled according to our Privacy Policy and retention requirements.",
        ],
      },
      {
        title: "11.2 Termination by Unimerg",
        content: [
          "We may terminate or suspend your account at any time, with or without cause, with or without notice. Grounds for termination include but are not limited to: violation of these Terms, fraudulent or illegal activity, failure to pass verification, quality issues, extended inactivity, abusive behavior, or business reasons.",
          "In cases of serious violations (fraud, illegal activity, harassment), termination is immediate and without prior notice. For less serious violations, we may provide warning and opportunity to cure.",
        ],
      },
      {
        title: "11.3 Effect of Termination",
        content: [
          "Upon termination: your right to access the Services immediately ceases, you must stop using the Services and any Confidential Information, we may delete your account data (subject to retention requirements), and forfeiture provisions for earnings may apply.",
          "The following provisions survive termination: confidentiality obligations, intellectual property assignments, limitation of liability, indemnification, dispute resolution, and any other provisions that by their nature should survive.",
        ],
      },
      {
        title: "11.4 Account Suspension",
        content: [
          "We may suspend your account pending investigation of potential violations. During suspension: you may not access the Services, earnings accumulation stops, and pending payments may be held.",
          "We will attempt to notify you of suspension and provide opportunity to respond. If the investigation clears you, your account will be restored and any held payments released.",
        ],
      },
    ],
  },
  {
    id: "disclaimers",
    title: "12. Disclaimers & Limitations of Liability",
    content: [
      "Please read this section carefully as it limits our liability to you.",
    ],
    subsections: [
      {
        title: "12.1 Disclaimer of Warranties",
        content: [
          'THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.',
          "We do not warrant that: the Services will meet your requirements, the Services will be uninterrupted, timely, secure, or error-free, results from the Services will be accurate or reliable, or any errors will be corrected.",
        ],
      },
      {
        title: "12.2 Limitation of Liability",
        content: [
          "TO THE MAXIMUM EXTENT PERMITTED BY LAW, APEX AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AND AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, USE, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICES.",
          "OUR TOTAL LIABILITY FOR ALL CLAIMS ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICES SHALL NOT EXCEED THE GREATER OF: (A) THE AMOUNTS PAID TO YOU IN THE SIX (6) MONTHS PRIOR TO THE CLAIM, OR (B) ONE HUNDRED DOLLARS ($100).",
          "THESE LIMITATIONS APPLY REGARDLESS OF THE THEORY OF LIABILITY AND EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.",
        ],
      },
      {
        title: "12.3 Basis of the Bargain",
        content: [
          "You acknowledge that we have set our prices and entered into these Terms in reliance upon the disclaimers and limitations of liability in this section, which allocate risk between us and form an essential basis of the bargain between us.",
        ],
      },
      {
        title: "12.4 Jurisdictional Limitations",
        content: [
          "Some jurisdictions do not allow the exclusion of certain warranties or the limitation of certain damages. In such jurisdictions, our liability is limited to the maximum extent permitted by law.",
        ],
      },
    ],
  },
  {
    id: "indemnification",
    title: "13. Indemnification",
    content: [
      "You agree to indemnify, defend, and hold harmless Unimerg and its officers, directors, employees, agents, affiliates, successors, and assigns from and against any and all claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising out of or related to:",
    ],
    subsections: [
      {
        title: "13.1 Scope of Indemnification",
        content: [
          "Your use of the Services or any activity under your account.",
          "Your violation of these Terms or any applicable law or regulation.",
          "Your violation of any third-party right, including intellectual property, privacy, or publicity rights.",
          "Any content you submit, post, or transmit through the Services.",
          "Any misrepresentation made by you.",
          "Your tax obligations or failure to comply with tax requirements.",
        ],
      },
      {
        title: "13.2 Procedure",
        content: [
          "We will provide you with notice of any claim subject to indemnification. You will not settle any claim without our prior written consent. We reserve the right to assume exclusive defense and control of any matter subject to indemnification by you, at your expense.",
        ],
      },
    ],
  },
  {
    id: "dispute-resolution",
    title: "14. Dispute Resolution & Arbitration",
    content: [
      "PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS, INCLUDING YOUR RIGHT TO FILE A LAWSUIT IN COURT.",
    ],
    subsections: [
      {
        title: "14.1 Informal Resolution",
        content: [
          "Before filing any formal dispute, you agree to attempt to resolve the dispute informally by contacting us at legal@unimerg.com. We will attempt to resolve the dispute within sixty (60) days. If the dispute is not resolved, you may proceed to arbitration.",
        ],
      },
      {
        title: "14.2 Binding Arbitration",
        content: [
          "Any dispute arising out of or relating to these Terms or the Services shall be resolved by binding arbitration administered by the American Arbitration Association (AAA) under its Commercial Arbitration Rules.",
          "The arbitration will be conducted in the English language, in Delaware, USA (or via telephone/video conference if you prefer). The arbitrator's decision will be final and binding and may be entered as a judgment in any court of competent jurisdiction.",
        ],
      },
      {
        title: "14.3 Class Action Waiver",
        content: [
          "YOU AND APEX AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE ACTION.",
          "The arbitrator may not consolidate more than one person's claims and may not preside over any form of representative or class proceeding.",
        ],
      },
      {
        title: "14.4 Exceptions",
        content: [
          "Notwithstanding the above, either party may seek injunctive or other equitable relief in any court of competent jurisdiction to prevent the actual or threatened infringement of intellectual property rights.",
          "Claims that qualify for small claims court may be brought in small claims court instead of arbitration.",
        ],
      },
      {
        title: "14.5 Opt-Out Right",
        content: [
          "You may opt out of this arbitration agreement by sending written notice to legal@unimerg.com within thirty (30) days of creating your account. The notice must include your name, address, email, and a clear statement that you wish to opt out of the arbitration agreement.",
        ],
      },
      {
        title: "14.6 Governing Law",
        content: [
          "These Terms and any dispute arising hereunder shall be governed by the laws of the State of Delaware, USA, without regard to its conflict of laws principles. The United Nations Convention on Contracts for the International Sale of Goods does not apply.",
        ],
      },
    ],
  },
  {
    id: "modifications",
    title: "15. Modifications to Terms",
    content: [
      'We reserve the right to modify these Terms at any time. When we make changes, we will update the "Last Updated" date at the top of these Terms.',
    ],
    subsections: [
      {
        title: "15.1 Notice of Changes",
        content: [
          "For material changes, we will provide notice through: email to the address associated with your account, prominent notice on our website or within the Services, or push notification through our applications.",
          "Your continued use of the Services after the effective date of any modifications constitutes your acceptance of the modified Terms.",
        ],
      },
      {
        title: "15.2 Rejection of Changes",
        content: [
          "If you do not agree to the modified Terms, you must stop using the Services and terminate your account before the effective date of the changes. Continued use after the effective date constitutes acceptance.",
        ],
      },
    ],
  },
  {
    id: "contact",
    title: "16. Contact Information",
    content: [
      "If you have any questions about these Terms, please contact us:",
    ],
    subsections: [
      {
        title: "16.1 General Inquiries",
        content: [
          "Unimerg Inc.",
          "123 Innovation Drive, Suite 500",
          "San Francisco, CA 94105",
          "United States",
          "",
          "Email: legal@unimerg.com",
          "Support: support@unimerg.com",
          "Phone: +1 (555) 123-4567",
        ],
      },
      {
        title: "16.2 Privacy Inquiries",
        content: [
          "For privacy-related questions or to exercise your data rights:",
          "Email: privacy@unimerg.com",
          "Data Protection Officer: dpo@unimerg.com",
        ],
      },
      {
        title: "16.3 Legal Notices",
        content: [
          "Legal notices should be sent to:",
          "Unimerg Inc.",
          "Attn: Legal Department",
          "123 Innovation Drive, Suite 500",
          "San Francisco, CA 94105",
          "Email: legal@unimerg.com",
        ],
      },
    ],
  },
];

export const acknowledgmentText = `By clicking "I Accept" or by accessing or using the Services, I acknowledge that I have read, understood, and agree to be bound by these Terms of Service, the Privacy Policy, and all other policies referenced herein. I understand that this is a legally binding agreement and that I am waiving certain legal rights, including the right to participate in class actions and the right to a jury trial for most disputes.`;
